import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FlattenedSchemaItem, SchemaApiValue, StructureColumnTypeEnum } from "../@shared/Models/Models";
import { filterHierarchy, getSchemaNamesDictionary, isOptionalSchemaItem, parseSchemaItemValue } from "../@shared/schemaUtils";
import { DataProviderInstance } from "../@shared/services/DataProvider";
import { NotificationModel, NotificationModelType } from "../reducers/main/mainModel";
import { loadOpenedSchema, onThrowAlert } from "../sagas/dataSaga/dataSagaActions";

export type UseCollectDataItemResult = {
    fields: FlattenedSchemaItem[];
    values: Record<string, string>;
    errors: Record<string, string>;
    isForMetrics: boolean;
    isSaving: boolean;
    isValid: boolean;
    onChangeValue: (key: string, value: string) => void;
    onChangeIsForMetrics: (isForMetrics: boolean) => void;
    onSave: () => Promise<void>;
}

export const useCollectDataItem = (
    schema: SchemaApiValue | undefined,
    isOpen: boolean,
    onSaved: () => void
): UseCollectDataItemResult => {
    const dispatch = useDispatch();
    const [values, setValues] = useState<Record<string, string>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [isForMetrics, setIsForMetrics] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const fields = useMemo(() => {
        if (!schema?.schemaItems) return [];
        const filtered = filterHierarchy(schema.schemaItems, [StructureColumnTypeEnum.Feature, StructureColumnTypeEnum.Info]);
        return getSchemaNamesDictionary(filtered)
            .slice()
            .sort((a, b) => a.key.localeCompare(b.key));
    }, [schema]);

    useEffect(() => {
        if (!isOpen) return;
        setValues({});
        setTouched({});
        setIsForMetrics(false);
    }, [isOpen]);

    const { data, errors } = useMemo(() => {
        const data: Record<string, unknown> = {};
        const errors: Record<string, string> = {};

        fields.forEach(field => {
            const rawValue = values[field.key] ?? "";
            if (!rawValue.trim() && isOptionalSchemaItem(field.schemaItem)) return;

            const result = parseSchemaItemValue(field.schemaItem, rawValue);
            if (result.error) {
                errors[field.key] = result.error;
                return;
            }
            data[field.key] = result.value;
        });

        return { data, errors };
    }, [fields, values]);

    const onChangeValue = useCallback((key: string, value: string) => {
        setValues(prev => ({ ...prev, [key]: value }));
        setTouched(prev => ({ ...prev, [key]: true }));
    }, []);

    const onSave = useCallback(async () => {
        if (Object.keys(errors).length > 0) {
            setTouched(Object.fromEntries(fields.map(field => [field.key, true])));
            return;
        }

        if (!schema?.id) return;

        try {
            setIsSaving(true);
            const result = await DataProviderInstance.createItem({
                schemaId: schema.id,
                isForMetrics: isForMetrics,
                data
            });

            if (result.status !== 200) {
                dispatch(onThrowAlert({
                    message: typeof result.data === "string" ? result.data : JSON.stringify(result.data),
                    lifeTime: 10000
                } as NotificationModel));
                return;
            }

            dispatch(onThrowAlert({
                message: "Data item successfully saved",
                lifeTime: 3000,
                type: NotificationModelType.Success
            } as NotificationModel));
            dispatch(loadOpenedSchema(schema.id));
            onSaved();
        }
        catch (e) {
            dispatch(onThrowAlert({
                message: (e as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsSaving(false);
        }
    }, [data, dispatch, errors, fields, isForMetrics, onSaved, schema]);

    const visibleErrors = useMemo(() => {
        return Object.fromEntries(Object.entries(errors).filter(([key]) => touched[key]));
    }, [errors, touched]);

    return {
        fields,
        values,
        errors: visibleErrors,
        isForMetrics,
        isSaving,
        isValid: Object.keys(errors).length === 0,
        onChangeValue,
        onChangeIsForMetrics: setIsForMetrics,
        onSave
    };
}
